import React, { useCallback, useEffect, useRef, useState } from "react";
import _ from "lodash";
import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Group,
  Paper,
  Text,
  TextInput,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import FileList from "./fileList";
import { saveFormFields } from "../helpers/formFieldSave";

const newSectionId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const getFileGroups = (form) => _.get(form.values, "fileGroups") || [];

const SectionFileList = React.memo(
  ({ form, initialFiles, disabled, onFilesChange }) => (
    <FileList
      form={form}
      name="files"
      disabled={disabled}
      enableValidate
      manageFilesLocally
      sectionFiles={initialFiles}
      onFilesChange={onFilesChange}
      showNoFilesMessage
      height={400}
    />
  ),
  (prev, next) =>
    prev.initialFiles === next.initialFiles &&
    prev.disabled === next.disabled &&
    prev.onFilesChange === next.onFilesChange
);

const UserFileGroupList = ({
  form,
  disabled = false,
  consultantView = false,
}) => {
  const { t } = useTranslation();
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const skipBlurCommitRef = useRef(false);
  const recordId = form.values?._id;
  const reloadKey = form._reloadKey ?? 0;
  const [fileGroups, setFileGroups] = useState(() => getFileGroups(form));
  const fileGroupsRef = useRef(fileGroups);
  const filesChangeHandlers = useRef({});

  fileGroupsRef.current = fileGroups;

  useEffect(() => {
    const next = getFileGroups(form);
    setFileGroups(next);
    fileGroupsRef.current = next;
    filesChangeHandlers.current = {};
  }, [recordId, reloadKey]);

  const persistQuiet = useCallback(
    (nextGroups) => {
      fileGroupsRef.current = nextGroups;
      return saveFormFields(form, { fileGroups: nextGroups });
    },
    [form]
  );

  const setFileGroupsAndPersist = useCallback(
    (nextGroups) => {
      setFileGroups(nextGroups);
      fileGroupsRef.current = nextGroups;
      return persistQuiet(nextGroups);
    },
    [persistQuiet]
  );

  const getFilesChangeHandler = useCallback(
    (sectionId) => {
      if (!filesChangeHandlers.current[sectionId]) {
        filesChangeHandlers.current[sectionId] = (newFiles) => {
          const next = fileGroupsRef.current.map((group) =>
            group.id === sectionId ? { ...group, files: newFiles } : group
          );
          persistQuiet(next);
        };
      }
      return filesChangeHandlers.current[sectionId];
    },
    [persistQuiet]
  );

  const sections = fileGroups
    .map((group, index) => ({ group, index }))
    .filter(({ group }) => !consultantView || group.shareWithConsultant);

  const addSection = () => {
    setFileGroupsAndPersist([
      ...fileGroupsRef.current,
      {
        id: newSectionId(),
        name: t("New Section"),
        shareWithConsultant: false,
        files: [],
        at: new Date(),
      },
    ]);
  };

  const updateSectionMeta = (sectionId, updates) => {
    setFileGroupsAndPersist(
      fileGroupsRef.current.map((group) =>
        group.id === sectionId ? { ...group, ...updates } : group
      )
    );
  };

  const deleteSection = (index) => {
    const group = fileGroupsRef.current[index];
    if (!group) return;

    const hasFiles = !_.isEmpty(group.files);
    const message = hasFiles
      ? t("Section has files. Delete anyway?")
      : t("Delete section?");

    if (!window.confirm(message)) return;

    delete filesChangeHandlers.current[group.id];
    const next = [...fileGroupsRef.current];
    next.splice(index, 1);
    setFileGroupsAndPersist(next);
  };

  const startEditing = (group) => {
    if (disabled || consultantView) return;
    setEditingSectionId(group.id);
    setEditingName(group.name || "");
  };

  const commitEditing = (sectionId) => {
    const name = editingName.trim();
    if (name) updateSectionMeta(sectionId, { name });
    setEditingSectionId(null);
    setEditingName("");
  };

  const canEdit = !disabled && !consultantView;

  return (
    <Box>
      {canEdit && (
        <Group position="right" mb="md">
          <Button size="xs" onClick={addSection}>
            {t("Add Section")}
          </Button>
        </Group>
      )}

      {consultantView && sections.length === 0 && (
        <Text size="sm" color="dimmed">
          {t("No files shared with you")}
        </Text>
      )}

      {sections.map(({ group, index }) => (
        <Paper key={group.id} shadow="md" p="xl" radius={0} mb="xl">
          <Group position="apart" mb="sm" noWrap>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {editingSectionId === group.id ? (
                <TextInput
                  size="xs"
                  value={editingName}
                  autoFocus
                  onChange={(e) => setEditingName(e.currentTarget.value)}
                  onBlur={() => {
                    if (skipBlurCommitRef.current) {
                      skipBlurCommitRef.current = false;
                      return;
                    }
                    commitEditing(group.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      skipBlurCommitRef.current = true;
                      commitEditing(group.id);
                    }
                    if (e.key === "Escape") {
                      skipBlurCommitRef.current = true;
                      setEditingSectionId(null);
                      setEditingName("");
                    }
                  }}
                />
              ) : (
                <Text
                  size="sm"
                  weight={600}
                  sx={{ cursor: canEdit ? "pointer" : "default" }}
                  onDoubleClick={() => startEditing(group)}
                >
                  {group.name || t("New Section")}
                </Text>
              )}
            </Box>

            {canEdit && (
              <Group spacing="sm" noWrap>
                <Checkbox
                  size="xs"
                  label={t("Share with consultant")}
                  checked={!!group.shareWithConsultant}
                  onChange={(e) =>
                    updateSectionMeta(group.id, {
                      shareWithConsultant: e.currentTarget.checked,
                    })
                  }
                />
                <ActionIcon
                  size="sm"
                  color="red"
                  onClick={() => deleteSection(index)}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Group>
            )}
          </Group>

          {disabled ? (
            <FileList
              staticFiles={group.files || []}
              disabled
              enableValidate
              manageFilesLocally
              showNoFilesMessage
              height={400}
            />
          ) : (
            <SectionFileList
              form={form}
              initialFiles={group.files}
              disabled={false}
              onFilesChange={getFilesChangeHandler(group.id)}
            />
          )}
        </Paper>
      ))}
    </Box>
  );
};

export default UserFileGroupList;
